import React, { type FC } from "react";
import classNames from "classnames";
import type { ApiOmpBoState } from "@src/api/bo";
import "./BusinessBoStateValue.scss";

/** Минимальный контракт состояния БО для отображения. */
export type BusinessBoStateValueState = Pick<ApiOmpBoState, "ShortName" | "Name">;

/** Источник подписи состояния БО. */
export type BusinessBoStateLabelSource = "short" | "name" | "auto";

/** Пропсы компонента {@link BusinessBoStateValue}. */
export interface BusinessBoStateValueProps {
    /** Состояние БО. */
    state?: BusinessBoStateValueState | null;
    /**
     * Источник подписи: `short`, `name` или `auto` (`ShortName`, затем `Name`). \
     * @default "auto"
     */
    labelSource?: BusinessBoStateLabelSource;
    /** Текст или узел для пустого состояния. @default "—" */
    fallback?: React.ReactNode;
    /** Дополнительный CSS-класс корневого контейнера. */
    className?: string;
}

function resolveTextPart(value: string | null | undefined): string | null {
    const text = value?.trim();
    return text === "" ? null : (text ?? null);
}

function resolveBoStateLabel(
    state: BusinessBoStateValueState | null | undefined,
    labelSource: BusinessBoStateLabelSource,
): string | null {
    const shortName = resolveTextPart(state?.ShortName);
    const name = resolveTextPart(state?.Name);

    if (labelSource === "name") {
        return name;
    }

    if (labelSource === "short") {
        return shortName;
    }

    return name ?? shortName;
}

/**
 * Формирует plain-text представление состояния БО.
 * @param state состояние БО
 * @param labelSource источник подписи
 * @returns подпись состояния или пустая строка
 */
export function formatBoStateText(
    state: BusinessBoStateValueState | null | undefined,
    labelSource: BusinessBoStateLabelSource = "auto",
): string {
    return resolveBoStateLabel(state, labelSource) ?? "";
}

/**
 * Read-only отображение состояния БО.
 *
 * @example
 * ```tsx
 * <BusinessBoStateValue state={order.State} />
 * <BusinessBoStateValue state={card.Bo?.State} labelSource="short" />
 * ```
 */
export const BusinessBoStateValue: FC<BusinessBoStateValueProps> = ({
    state,
    labelSource = "auto",
    fallback = "—",
    className,
}) => {
    const label = resolveBoStateLabel(state, labelSource);

    if (label == null) {
        return <span className={className}>{fallback}</span>;
    }

    return (
        <span className={classNames("business_bo_state_value", className)}>
            <span className="business_bo_state_value__label">{label}</span>
        </span>
    );
};
